import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, X, MessageSquare, CheckCircle } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useNotifications } from '../../context/NotificationContext';
import { supabase } from '../../utils/supabase';
import { format } from 'date-fns';

export interface AdminHeaderProps {
  onToggleSidebar: () => void;
}

export function AdminHeader({ onToggleSidebar }: AdminHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { supportTickets, unreadCount, markTicketAsRead, markAllTicketsAsRead } = useNotifications();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [bellCount, setBellCount] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);
  
  const pageName = location.pathname.split('/').pop();
  const formattedPageName = pageName?.charAt(0).toUpperCase() + pageName?.slice(1);

  // Fetch unread counts from server
  const updateCount = async () => {
    try {
      console.log('Fetching unread counts...');
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unread-counts`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Unread counts request failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Unread counts received:', result);

      if (result.success && result.data) {
        setOrderCount(result.data.new_orders);
        // Add support ticket count from context
        const totalTicketCount = supportTickets.filter(ticket => !ticket.isRead).length;
        setTicketCount(totalTicketCount);
        setBellCount(result.data.new_orders + totalTicketCount);
      }
    } catch (error) {
      console.error('Error fetching unread counts:', error);
      // Fallback to local calculation
      const totalTicketCount = supportTickets.filter(ticket => !ticket.isRead).length;
      setTicketCount(totalTicketCount);
      setBellCount(totalTicketCount);
    }
  };

  // Set up real-time listeners for order and ticket changes
  useEffect(() => {
    // Initial count update
    updateCount();

    // Set up real-time subscription for new orders
    const subscription = supabase
      .channel('notification_updates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        () => {
          console.log('New order detected, updating counts');
          updateCount();
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          console.log('Order status changed, updating counts:', payload);
          updateCount();
        }
      )
      .subscribe();

    // Update counts every 30 seconds as backup
    const interval = setInterval(updateCount, 30000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  // Update ticket count when support tickets change
  useEffect(() => {
    const totalTicketCount = supportTickets.filter(ticket => !ticket.isRead).length;
    setTicketCount(totalTicketCount);
    setBellCount(orderCount + totalTicketCount);
  }, [supportTickets, orderCount]);

  const handleViewTicket = (ticketId: string) => {
    markTicketAsRead(ticketId);
    setIsPopoverOpen(false);
    navigate('/admin/support');
  };

  const handleViewAllTickets = () => {
    markAllTicketsAsRead();
    setIsPopoverOpen(false);
    navigate('/admin/support');
  };

  const handleViewOrders = () => {
    setIsPopoverOpen(false);
    navigate('/admin/orders');
  };

  return (
    <header className="bg-white shadow">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center">
          <button
            onClick={onToggleSidebar}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg border border-gray-200 lg:hidden"
            aria-label="Toggle sidebar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900 lg:ml-0 ml-4">
            {formattedPageName || 'Dashboard'}
          </h1>
        </div>

        <div className="flex items-center space-x-4">
          <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                className="relative p-2 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {bellCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 flex items-center justify-center text-xs font-bold text-white animate-pulse">
                    {bellCount > 99 ? '99+' : bellCount}
                  </span>
                )}
                <Bell className="h-6 w-6" />
              </button>
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                className="w-full max-w-sm bg-white rounded-lg shadow-lg border border-gray-200 animate-in fade-in-0 zoom-in-95"
                sideOffset={5}
                align="end"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
                    <div className="flex items-center space-x-2">
                      {bellCount > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                          {bellCount} new
                        </span>
                      )}
                      <Popover.Close className="text-gray-400 hover:text-gray-500">
                        <X className="w-5 h-5" />
                      </Popover.Close>
                    </div>
                  </div>

                  <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {/* New Orders Section */}
                    {orderCount > 0 && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <MessageSquare className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-blue-900">
                                {orderCount} New Order{orderCount !== 1 ? 's' : ''}
                              </p>
                              <p className="text-xs text-blue-700">
                                Pending orders require attention
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={handleViewOrders}
                            className="text-sm font-medium text-blue-700 hover:text-blue-800"
                          >
                            View Orders
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Support Tickets Section */}
                    {supportTickets.length > 0 ? (
                      supportTickets
                        .filter(ticket => !ticket.isRead)
                        .slice(0, 3)
                        .map(ticket => (
                          <div
                            key={ticket.id}
                            className="bg-yellow-50 rounded-lg p-4"
                          >
                            <div className="flex items-start">
                              <MessageSquare className="h-5 w-5 text-yellow-600 mt-1" />
                              <div className="ml-3 flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-yellow-900">
                                    {ticket.customer.subject || 'Support Request'}
                                  </p>
                                  <span className="text-xs text-yellow-700">
                                    {format(new Date(ticket.createdAt), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                <p className="mt-1 text-sm text-yellow-800">
                                  From: {ticket.customer.name}
                                </p>
                                <button
                                  onClick={() => handleViewTicket(ticket.id)}
                                  className="mt-2 text-sm font-medium text-yellow-700 hover:text-yellow-800"
                                >
                                  View Ticket
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : null}

                    {/* Show all tickets link if there are more */}
                    {ticketCount > 3 && (
                      <div className="text-center">
                        <button
                          onClick={handleViewAllTickets}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View all {ticketCount} support tickets
                        </button>
                      </div>
                    )}

                    {/* Empty state */}
                    {bellCount === 0 && (
                      <div className="text-center py-6 text-gray-500">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p>All caught up!</p>
                        <p className="text-xs mt-1">No new notifications</p>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  {bellCount > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex space-x-2">
                        {orderCount > 0 && (
                          <button
                            onClick={handleViewOrders}
                            className="flex-1 bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded-md hover:bg-blue-700"
                          >
                            View Orders ({orderCount})
                          </button>
                        )}
                        {ticketCount > 0 && (
                          <button
                            onClick={handleViewAllTickets}
                            className="flex-1 bg-yellow-600 text-white text-sm font-medium py-2 px-3 rounded-md hover:bg-yellow-700"
                          >
                            View Tickets ({ticketCount})
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>

          <div className="flex items-center">
            <img
              className="h-8 w-8 rounded-full"
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
              alt="Admin"
            />
            <span className="ml-3 text-sm font-medium text-gray-700 hidden sm:inline">Admin User</span>
          </div>
        </div>
      </div>
    </header>
  );
}